import { useEffect, useRef, useState, useCallback } from 'react';

// Voice-to-text using the browser's Web Speech API.
// Supported in Chrome/Edge (desktop + Android). Safari/iOS support is partial,
// so `supported` is false there and callers can hide the mic button.
export function useSpeechToText({ lang = 'en-IN', onResult } = {}) {
  const SpeechRecognition =
    typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const supported = !!SpeechRecognition;
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    if (!supported) return;
    const rec = new SpeechRecognition();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = false;

    rec.onresult = (e) => {
      let chunk = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) chunk += e.results[i][0].transcript;
      }
      if (chunk && onResultRef.current) onResultRef.current(chunk.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);

    recRef.current = rec;
    return () => { try { rec.stop(); } catch (_) {} };
  }, [supported, lang, SpeechRecognition]);

  const start = useCallback(() => {
    if (!recRef.current || listening) return;
    try { recRef.current.start(); setListening(true); } catch (_) {}
  }, [listening]);

  const stop = useCallback(() => {
    if (!recRef.current) return;
    try { recRef.current.stop(); } catch (_) {}
    setListening(false);
  }, []);

  const toggle = useCallback(() => {
    listening ? stop() : start();
  }, [listening, start, stop]);

  return { supported, listening, start, stop, toggle };
}
